// Tile geometry for the dashboard designer's layout canvas — pure functions,
// no React. Positions are 12-column grid units in quarter steps (DASH_STEP);
// Y grows downward without limit. Every function returns NEW tiles and never
// mutates its input.
//
// Rules (docs/plans/dashboard-designer-redesign.md):
//   • push-down: tiles the author just moved/resized are fixed; any other
//     tile they overlap that starts at or below them moves to their bottom,
//     cascading. A tile ABOVE a moved one is never moved.
//   • a top-edge resize stops at the tile above, so it never moves anything up.
//   • empty space = a full-width band no tile covers, between tiles; removing
//     it moves everything below up by the band's height.
//   • arrange: 'gaps' moves tiles up into empty space (sizes and columns kept);
//     'rows' lines tiles up left→right, top→bottom in their current order;
//     'fill' does 'rows' then widens each row to the full width and evens out
//     its height.

import type { DashboardVisual } from './types'

export const COLS = 12
export const STEP = 0.25
export const MIN_SIZE = 0.5
const E = 1e-6

export type ResizeMode = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
export type ArrangeMode = 'gaps' | 'rows' | 'fill'

export function snap(v: number): number {
  return Math.round(v / STEP) * STEP
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

interface Box { X: number; Y: number; WIDTH: number; HEIGHT: number }

function overlaps(a: Box, b: Box): boolean {
  return a.X < b.X + b.WIDTH - E && b.X < a.X + a.WIDTH - E
    && a.Y < b.Y + b.HEIGHT - E && b.Y < a.Y + a.HEIGHT - E
}

function cloneTiles(tiles: readonly DashboardVisual[]): DashboardVisual[] {
  return tiles.map((t) => ({ ...t, POSITION: { ...t.POSITION } }))
}

/** Push tiles overlapped by `anchorIds` downward, cascading. */
export function pushDown(tiles: readonly DashboardVisual[], anchorIds: readonly string[]): DashboardVisual[] {
  const out = cloneTiles(tiles)
  const fixed = new Set(anchorIds)
  const queue = [...anchorIds]
  // Bounded: every step moves a tile below a fixed one, finitely many tiles.
  let guard = 0
  while (queue.length && guard++ < 2000) {
    const id = queue.shift()!
    const m = out.find((t) => t.VISUAL_ID === id)
    if (!m) continue
    for (const t of out) {
      if (t.VISUAL_ID === m.VISUAL_ID || fixed.has(t.VISUAL_ID)) continue
      if (overlaps(m.POSITION, t.POSITION) && t.POSITION.Y >= m.POSITION.Y - E) {
        t.POSITION.Y = snap(m.POSITION.Y + m.POSITION.HEIGHT)
        queue.push(t.VISUAL_ID)
      }
    }
  }
  return out
}

/** Move a group of tiles by (dx, dy) grid units from their ORIGINAL places,
 *  clamped so the whole group stays on the canvas, then push down. */
export function moveTiles(
  orig: readonly DashboardVisual[], ids: readonly string[], dx: number, dy: number,
): DashboardVisual[] {
  const out = cloneTiles(orig)
  const grp = out.filter((t) => ids.includes(t.VISUAL_ID))
  if (!grp.length) return out
  const minX = Math.min(...grp.map((t) => t.POSITION.X))
  const maxR = Math.max(...grp.map((t) => t.POSITION.X + t.POSITION.WIDTH))
  const minY = Math.min(...grp.map((t) => t.POSITION.Y))
  const cdx = clamp(snap(dx), -minX, COLS - maxR)
  const cdy = Math.max(snap(dy), -minY)
  for (const t of grp) {
    t.POSITION.X = snap(t.POSITION.X + cdx)
    t.POSITION.Y = snap(t.POSITION.Y + cdy)
  }
  settleBelow(out, ids)
  return pushDown(out, ids)
}

/** A moved group dropped onto the lower part of a tile that starts ABOVE it
 *  slides down to that tile's bottom (rigidly), since tiles above never move.
 *  Mutates `tiles`. */
function settleBelow(tiles: DashboardVisual[], ids: readonly string[]): void {
  const group = tiles.filter((t) => ids.includes(t.VISUAL_ID))
  const others = tiles.filter((t) => !ids.includes(t.VISUAL_ID))
  for (let guard = 0; guard < 500; guard++) {
    let shift = 0
    for (const m of group) {
      for (const o of others) {
        if (o.POSITION.Y < m.POSITION.Y - E && overlaps(m.POSITION, o.POSITION)) {
          shift = Math.max(shift, o.POSITION.Y + o.POSITION.HEIGHT - m.POSITION.Y)
        }
      }
    }
    if (shift <= E) return
    for (const m of group) m.POSITION.Y = snap(m.POSITION.Y + shift)
  }
}

/** Resize one tile from an edge or corner by (dx, dy) grid units from its
 *  ORIGINAL box, then push down. */
export function resizeTile(
  orig: readonly DashboardVisual[], id: string, mode: ResizeMode, dx: number, dy: number,
): DashboardVisual[] {
  const out = cloneTiles(orig)
  const t = out.find((x) => x.VISUAL_ID === id)
  const o = orig.find((x) => x.VISUAL_ID === id)
  if (!t || !o) return out
  const p = o.POSITION
  const right = p.X + p.WIDTH
  const bottom = p.Y + p.HEIGHT
  // A side edge stops at a tile that starts above this one and reaches down
  // beside it — that tile can't be pushed (tiles above never move).
  const besideAbove = orig.filter((a) => a.VISUAL_ID !== id
    && a.POSITION.Y < p.Y - E && a.POSITION.Y + a.POSITION.HEIGHT > p.Y + E)
  const rightLimit = besideAbove
    .filter((a) => a.POSITION.X >= right - E)
    .reduce((m, a) => Math.min(m, a.POSITION.X), COLS)
  const leftLimit = besideAbove
    .filter((a) => a.POSITION.X + a.POSITION.WIDTH <= p.X + E)
    .reduce((m, a) => Math.max(m, a.POSITION.X + a.POSITION.WIDTH), 0)
  if (mode.includes('e')) t.POSITION.WIDTH = clamp(snap(p.WIDTH + dx), MIN_SIZE, rightLimit - p.X)
  if (mode.includes('w')) {
    const nx = clamp(snap(p.X + dx), leftLimit, right - MIN_SIZE)
    t.POSITION.X = nx
    t.POSITION.WIDTH = right - nx
  }
  if (mode.includes('s')) t.POSITION.HEIGHT = Math.max(MIN_SIZE, snap(p.HEIGHT + dy))
  if (mode.includes('n')) {
    // The top edge stops at the tile above, so nothing above is ever moved.
    const floor = orig.reduce((m, a) => {
      if (a.VISUAL_ID === id) return m
      const ap = a.POSITION
      const above = ap.Y + ap.HEIGHT <= p.Y + E && ap.X < right - E && p.X < ap.X + ap.WIDTH - E
      return above ? Math.max(m, ap.Y + ap.HEIGHT) : m
    }, 0)
    const ny = clamp(snap(p.Y + dy), floor, bottom - MIN_SIZE)
    t.POSITION.Y = ny
    t.POSITION.HEIGHT = bottom - ny
  }
  return pushDown(out, [id])
}

/** Set one field of a tile's box (inspector X/Y/W/H inputs), clamped. */
export function setTileBox(
  tiles: readonly DashboardVisual[], id: string, field: keyof Box, value: number,
): DashboardVisual[] {
  const out = cloneTiles(tiles)
  const t = out.find((x) => x.VISUAL_ID === id)
  if (!t || !Number.isFinite(value)) return out
  const v = snap(value)
  const p = t.POSITION
  if (field === 'X') p.X = clamp(v, 0, COLS - Math.min(p.WIDTH, COLS))
  else if (field === 'Y') p.Y = Math.max(0, v)
  else if (field === 'WIDTH') p.WIDTH = clamp(v, MIN_SIZE, COLS - p.X)
  else p.HEIGHT = Math.max(MIN_SIZE, v)
  settleBelow(out, [id])
  return pushDown(out, [id])
}

/** Full-width empty bands between tiles: [from, to) in grid rows. */
export function emptyBands(tiles: readonly DashboardVisual[]): Array<{ from: number; to: number }> {
  const spans = tiles
    .map((t) => [t.POSITION.Y, t.POSITION.Y + t.POSITION.HEIGHT] as const)
    .sort((a, b) => a[0] - b[0])
  const out: Array<{ from: number; to: number }> = []
  let reach = 0
  for (const [top, bot] of spans) {
    if (top > reach + E) out.push({ from: reach, to: top })
    reach = Math.max(reach, bot)
  }
  return out
}

/** Remove an empty band: every tile at or below its bottom moves up by its height. */
export function removeBand(tiles: readonly DashboardVisual[], from: number, to: number): DashboardVisual[] {
  const d = to - from
  return cloneTiles(tiles).map((t) => {
    if (t.POSITION.Y >= to - E) t.POSITION.Y = snap(Math.max(0, t.POSITION.Y - d))
    return t
  })
}

/** Arrange every tile automatically (see the module comment). */
export function arrangeTiles(tiles: readonly DashboardVisual[], mode: ArrangeMode): DashboardVisual[] {
  const order = cloneTiles(tiles).sort(
    (a, b) => (a.POSITION.Y - b.POSITION.Y) || (a.POSITION.X - b.POSITION.X),
  )
  const placed: DashboardVisual[] = []
  if (mode === 'gaps') {
    for (const t of order) {
      let y = t.POSITION.Y
      while (y > 0) {
        const ny = Math.max(0, snap(y - STEP))
        const probe = { ...t.POSITION, Y: ny }
        if (placed.some((o) => overlaps(o.POSITION, probe))) break
        y = ny
      }
      t.POSITION.Y = y
      placed.push(t)
    }
  } else {
    let x = 0
    let y = 0
    let rowH = 0
    let row: DashboardVisual[] = []
    const flush = () => {
      if (!row.length) return
      if (mode === 'fill') {
        const sum = row.reduce((s, t) => s + t.POSITION.WIDTH, 0)
        let cx = 0
        row.forEach((t, i) => {
          const w = i === row.length - 1
            ? COLS - cx
            : Math.max(MIN_SIZE, snap((t.POSITION.WIDTH * COLS) / sum))
          t.POSITION.X = cx
          t.POSITION.WIDTH = w
          t.POSITION.HEIGHT = rowH
          cx += w
        })
      }
      y += rowH
      x = 0
      rowH = 0
      row = []
    }
    for (const t of order) {
      const w = Math.min(t.POSITION.WIDTH, COLS)
      if (x + w > COLS + E) flush()
      t.POSITION.X = x
      t.POSITION.Y = y
      t.POSITION.WIDTH = w
      x += w
      rowH = Math.max(rowH, t.POSITION.HEIGHT)
      row.push(t)
      placed.push(t)
    }
    flush()
  }
  // Keep the model's original tile order (it is the saved order).
  return tiles.map((t) => placed.find((p) => p.VISUAL_ID === t.VISUAL_ID) ?? { ...t, POSITION: { ...t.POSITION } })
}

/** The row below the lowest tile (where a new tile goes). */
export function bottomRow(tiles: readonly DashboardVisual[]): number {
  return tiles.reduce((m, t) => Math.max(m, t.POSITION.Y + t.POSITION.HEIGHT), 0)
}

/** Nudge a set of tiles by (dx, dy), clamped to the canvas, then push down. */
export function nudgeTiles(
  tiles: readonly DashboardVisual[], ids: readonly string[], dx: number, dy: number,
): DashboardVisual[] {
  return moveTiles(tiles, ids, dx, dy)
}
